import { Column, Text } from "@/components";
import { racetrackLanes } from "@/lib/racetrackLanes";

// Spider rests in the web until someone finishes, then drops to their lane.
const WEB_RADIUS_PX = 34;
const WEB_SPOKE_COUNT = 7;
const WEB_RING_FRACTIONS = [0.35, 0.65, 0.95];
// The strand starts above the first lane (-top-8) so the web has room to hang.
const STRAND_OVERHANG_PX = 32;
// Spider hangs just above the finisher's pumpkin rather than covering it.
const SPIDER_HEIGHT_PX = 22;
const PUMPKIN_HALF_HEIGHT_PX = 12;
const SPIDER_DROP_MS = 1200;

// Half web fanning down from the strand's anchor point.
const webPoint = (spoke: number, radius: number) => {
  const angle = (Math.PI * spoke) / (WEB_SPOKE_COUNT - 1);
  return {
    x: WEB_RADIUS_PX + radius * Math.cos(angle),
    y: radius * Math.sin(angle),
  };
};

const SPOKES = Array.from({ length: WEB_SPOKE_COUNT }, (_, spoke) =>
  webPoint(spoke, WEB_RADIUS_PX),
);

// Each ring sags slightly toward the anchor between spokes, like real silk.
const RINGS = WEB_RING_FRACTIONS.map((fraction) => {
  const radius = WEB_RADIUS_PX * fraction;
  const start = webPoint(0, radius);
  const segments = Array.from({ length: WEB_SPOKE_COUNT - 1 }, (_, i) => {
    const sag = webPoint(i + 0.5, radius * 0.86);
    const end = webPoint(i + 1, radius);
    return `Q ${sag.x.toFixed(1)} ${sag.y.toFixed(1)} ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
  });
  return `M ${start.x.toFixed(1)} ${start.y.toFixed(1)} ${segments.join(" ")}`;
});

export const FinishSpiderWeb = ({
  laneCount,
  spiderLaneIndex,
}: {
  laneCount: number;
  spiderLaneIndex: number | null;
}) => {
  /* Helpers */
  const laneStridePx = racetrackLanes.PET_HEIGHT_PX + racetrackLanes.GAP_PX;
  const spiderTopPx =
    spiderLaneIndex === null
      ? (WEB_RADIUS_PX - SPIDER_HEIGHT_PX) / 2
      : STRAND_OVERHANG_PX +
        spiderLaneIndex * laneStridePx +
        racetrackLanes.PET_HEIGHT_PX / 2 -
        PUMPKIN_HALF_HEIGHT_PX -
        SPIDER_HEIGHT_PX;

  /* Render */
  return (
    <Column
      className="pointer-events-none absolute -top-8 bottom-0 right-4 w-0"
      style={{ zIndex: laneCount + 2 }}
    >
      <Column className="absolute inset-y-0 left-0 w-[1.5px] -translate-x-1/2 bg-gradient-to-b from-white/80 via-white/55 to-white/20" />
      <svg
        className="absolute left-0 top-0 -translate-x-1/2 overflow-visible"
        width={WEB_RADIUS_PX * 2}
        height={WEB_RADIUS_PX}
        viewBox={`0 0 ${(WEB_RADIUS_PX * 2).toString()} ${WEB_RADIUS_PX.toString()}`}
        fill="none"
        stroke="white"
        strokeOpacity={0.7}
        strokeWidth={1}
        strokeLinecap="round"
      >
        {SPOKES.map((point, i) => (
          <line key={i} x1={WEB_RADIUS_PX} y1={0} x2={point.x} y2={point.y} />
        ))}
        {RINGS.map((path, i) => (
          <path key={i} d={path} />
        ))}
      </svg>
      <Column
        className="absolute left-0 -translate-x-1/2"
        style={{
          top: spiderTopPx,
          transition: `top ${SPIDER_DROP_MS.toString()}ms ease-out`,
        }}
      >
        <Text className="leaderboard-spider-bob text-[22px] leading-none [filter:drop-shadow(0_0_4px_rgba(255,255,255,0.45))]">
          🕷️
        </Text>
      </Column>
    </Column>
  );
};
